import { Filter, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { CONTRATOS_ACTIVOS } from '../contratos';

interface ClientListAdvancedFiltersProps {
    onFilterSelect: (filter: FilterStatus) => void;
    currentFilter: FilterStatus;
}

type FilterStatus = 
    | 'all' 
    | 'active' 
    | 'inactive' 
    | 'waiting'
    | 'domiciliaciones'
    | 'otros-pagos'
    | 'sin-iban'
    | 'obs-cliente'
    | 'obs-suscripcion'
    | 'sin-contrato'
    | 'contrato-pendiente'
    | 'contrato-firmado'
    | 'contrato-sin-envio'
    | 'con-recuperaciones'
    | 'sin-correo'
    | 'no-invitados-portal'
    | 'pago-tarjeta'
    | 'pago-efectivo';

const ClientListAdvancedFilters: React.FC<ClientListAdvancedFiltersProps> = ({ 
    onFilterSelect, 
    currentFilter 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Cerrar el dropdown cuando se hace clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (filter: FilterStatus) => {
        onFilterSelect(filter);
        setIsOpen(false);
    };
    
    // Determinar si algún filtro avanzado está activo
    const isAdvancedFilterActive = [
        'waiting', 'domiciliaciones', 'otros-pagos', 
        'sin-iban', 'obs-cliente', 'obs-suscripcion',
        'sin-contrato', 'contrato-pendiente', 'contrato-firmado', 'contrato-sin-envio',
        'con-recuperaciones', 'sin-correo', 'no-invitados-portal', 'pago-tarjeta', 'pago-efectivo',
    ].includes(currentFilter);

    const opcion = (filter: FilterStatus, label: string) => (
        <button
            onClick={() => handleSelect(filter)}
            className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                currentFilter === filter ? 'bg-purple-100 font-medium' : ''
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="relative" ref={dropdownRef}>
        <button
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-1 px-3 py-2 rounded border ${
            isAdvancedFilterActive ? 'bg-purple-500 text-white' : 'bg-white text-gray-700'
            }`}
            title="Filtros avanzados"
        >
            <Filter size={16} />
            <span className="hidden sm:inline">Filtros avanzados</span>
            <ChevronDown size={14} />
        </button>

        {isOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-white border rounded-md shadow-lg z-10">
                <div className="py-1">
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Plazas</h3>
                    <button
                        onClick={() => handleSelect('waiting')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'waiting' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Clientes con plazas en espera
                    </button>
                    {opcion('con-recuperaciones', 'Con recuperaciones pendientes')}

                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-1">Contacto</h3>
                    {opcion('sin-correo', 'Sin correo (sin portal ni avisos)')}
                    {opcion('no-invitados-portal', 'No invitados al portal')}
                    
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-1">Suscripciones</h3>
                    <button
                        onClick={() => handleSelect('domiciliaciones')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'domiciliaciones' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Domiciliaciones
                    </button>
                    <button
                        onClick={() => handleSelect('otros-pagos')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'otros-pagos' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Otros pagos
                    </button>
                    <button
                        onClick={() => handleSelect('sin-iban')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'sin-iban' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Domiciliados sin IBAN
                    </button>
                    {opcion('pago-tarjeta', 'Pagan con tarjeta')}
                    {opcion('pago-efectivo', 'Pagan en efectivo')}
                    
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-1">Observaciones</h3>
                    <button
                        onClick={() => handleSelect('obs-cliente')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'obs-cliente' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Con Referencias en cliente
                    </button>
                    <button
                        onClick={() => handleSelect('obs-suscripcion')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'obs-suscripcion' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Con observaciones en Suscripción
                    </button>

                    {CONTRATOS_ACTIVOS && (<>
                    <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase border-t mt-1">Contratos</h3>
                    <button
                        onClick={() => handleSelect('sin-contrato')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'sin-contrato' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Sin contrato
                    </button>
                    <button
                        onClick={() => handleSelect('contrato-pendiente')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'contrato-pendiente' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Contrato pendiente de firma
                    </button>
                    <button
                        onClick={() => handleSelect('contrato-firmado')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'contrato-firmado' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        Contrato firmado
                    </button>
                    <button
                        onClick={() => handleSelect('contrato-sin-envio')}
                        className={`px-4 py-2 text-sm text-left w-full hover:bg-gray-100 ${
                            currentFilter === 'contrato-sin-envio' ? 'bg-purple-100 font-medium' : ''
                        }`}
                    >
                        🟡 Contrato sin enviar por correo
                    </button>
                    </>)}
                </div>
            </div>
        )}
        </div>
    );
};

export default ClientListAdvancedFilters;